import { useCallback, useMemo } from 'react'
import type { CategoryDTO, CheckoutProduct, Currency, Option } from 'ecosystem'
import type { GaPurchaseOrder } from '../../third-party'
import { createCategoryMap, ga4EcommerceItemsFactory } from '../utils'
import { useGtmEvents } from './useGtmEvents'

type UseGtmPurchaseEventParams = {
  categories: Option<CategoryDTO[]>
  currency: Currency
}

export const useGtmPurchaseEvent = ({ categories, currency }: UseGtmPurchaseEventParams) => {
  const { trackPurchaseEvent } = useGtmEvents()

  const categoryMap = useMemo(() => createCategoryMap(categories), [categories])

  const purchaseEvent = useCallback(
    (order: GaPurchaseOrder, items: CheckoutProduct[]) =>
      trackPurchaseEvent({
        // Non-GA4 extras live at the event root: a GA4 tag reading ecommerce
        // from the data layer takes the `ecommerce` object verbatim and would
        // drop anything non-standard hidden inside it.
        discount_total: order.discountAmount,
        shipping_method: order.shippingMethod,
        payment_method: order.paymentMethod,

        ecommerce: {
          transaction_id: order.id,
          currency: order.currency ?? currency,
          value: order.totalAmount ?? 0,
          tax: order.taxAmount,
          shipping: order.shippingPrice,
          coupon: order.discountCode,
          items: ga4EcommerceItemsFactory(items, categoryMap)
        }
      }),
    [categoryMap, currency, trackPurchaseEvent]
  )

  return {
    purchaseEvent
  }
}
